import {distinctUntilChanged} from 'rxjs/operators';
import {DiversityColors, DiversityOptions, DiversityTab} from '../../../../model';
import {BehaviorSubject} from 'rxjs/index';
import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

interface YearsAndQuarters {
  years: string[];
  quarters: string[];
}

interface StageStatus {
  submitted: boolean;
  interviewed: boolean;
  hired: boolean;
}

@Injectable()
export class DiversityInsightService {
  private defaultSelected: DiversityTab = {
    name: DiversityOptions.gender,
    data: []
  };

  colors: string[] = Object.values(DiversityColors);

  years = [
    (new Date()).getFullYear(),
    (new Date()).getFullYear() - 1,
    (new Date()).getFullYear() - 2,
    (new Date()).getFullYear() - 3,
    (new Date()).getFullYear() - 4,
  ];

  quarters = [
    this.Translate.instant('diversity.quarters.q4'),
    this.Translate.instant('diversity.quarters.q3'),
    this.Translate.instant('diversity.quarters.q2'),
    this.Translate.instant('diversity.quarters.q1'),
  ];

  private readonly _selectedDiversityTab = new BehaviorSubject(this.defaultSelected);
  readonly selectedDiversityTab$ = this._selectedDiversityTab.asObservable().pipe(distinctUntilChanged());

  private readonly _showNotEnoughInfoScreen = new BehaviorSubject(false);
  readonly showNotEnoughInfoScreen$ = this._showNotEnoughInfoScreen.asObservable().pipe(distinctUntilChanged());

  private readonly _selectedYearsAndQuartersUpdated = new BehaviorSubject({
    years: this.years.map((year) => `${year}`),
    quarters: this.quarters
  });
  readonly selectedYearsAndQuartersUpdated$ = this._selectedYearsAndQuartersUpdated.asObservable().pipe(distinctUntilChanged());

  private readonly _stageStatus = new BehaviorSubject({
    submitted: true,
    interviewed: true,
    hired: true
  });
  readonly stageStatus$ = this._stageStatus.asObservable().pipe(distinctUntilChanged());

  private readonly _hiddenDemographics = new BehaviorSubject([]);
  readonly hiddenDemographics$ = this._hiddenDemographics.asObservable().pipe(distinctUntilChanged());

  constructor(private Translate: TranslateService) {}

  set selectedDiversityTab(val: DiversityTab) {
    this._selectedDiversityTab.next(val);
  }

  get selectedDiversityTab(): DiversityTab {
    return this._selectedDiversityTab.getValue();
  }

  set dateSelection(val: YearsAndQuarters) {
    this._selectedYearsAndQuartersUpdated.next(val);
  }

  get dateSelection() {
    return this._selectedYearsAndQuartersUpdated.getValue();
  }

  set showNotEnoughInfoScreen(val: boolean) {
    this._showNotEnoughInfoScreen.next(val);
  }

  get showNotEnoughInfoScreen(): boolean {
    return this._showNotEnoughInfoScreen.getValue();
  }

  set stageStatus(val: StageStatus) {
    this._stageStatus.next(val);
  }

  get stageStatus() {
    return this._stageStatus.getValue();
  }

  set hiddenDemographics(val: string[]) {
    this._hiddenDemographics.next(val);
  }

  get hiddenDemographics() {
    return this._hiddenDemographics.getValue();
  }

  getStageDisplayStatus(label) {
    if (label.includes(' ')) {
      label = label.split(' ')[0];
    }
    switch (label.toLowerCase()) {
      case this.Translate.instant('diversity.submitted').toLowerCase():
        return this.stageStatus.submitted;
      case this.Translate.instant('diversity.interviewed').toLowerCase():
        return this.stageStatus.interviewed;
      case this.Translate.instant('diversity.hired').toLowerCase():
        return this.stageStatus.hired;
    }
  }

  getDemographicDisplayStatus(label) {
    return !this.hiddenDemographics.includes(label);
  }

  getDemographicLabelsForCategory(category: String): object[] {
    const diversityTranslations: object = this.Translate.instant(`diversity.${category}`);
    const labelKeys: string[] = Object.keys(diversityTranslations).filter(labelKey => labelKey !== 'label');
    const labelData = labelKeys.map((labelKey, i) => {
      return {text: diversityTranslations[labelKey](), color: this.colors[i]};
    });
    labelData.push({text: this.Translate.instant('diversity.unknown'), color: DiversityColors.gray});
    return labelData;
  }
}
